import React, { useEffect, useRef, useState } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import './ChangePasswordPage.css';

export type ChangePasswordPageState = 'form' | 'expired-token' | 'contact-support';

export interface ChangePasswordPageProps {
  /** Which top-level state to render. Picked server-side and passed through the template. */
  state: ChangePasswordPageState;
  /** Hex token from `?token=...`. Empty string in expired-password flow. Always rendered as hidden input in form state. */
  token?: string;
  /**
   * Server-side validation error code from a previous submit (form state only).
   * One of: "TOO SHORT:N", "NO SPECIAL", "NO DIGIT", "NO LOWERCASE", "NO UPPERCASE", "MISMATCH".
   */
  error?: string;
  /** Minimum password length used in the "TOO SHORT" message and short-circuit. */
  minPasswordLength: number;
  /** When true, render the "Your password has expired" notice above the form. */
  expiredPassword?: boolean;
  /** Override fetch for testing. */
  fetchFn?: typeof fetch;
}

/**
 * Maps the server-side validation error code to its verbatim legacy message.
 * Mirrors the `error.match('TOO SHORT:\d+')` cascade in the legacy template.
 */
function getServerErrorMessage(error: string, minPasswordLength: number): string | null {
  if (/^TOO SHORT:\d+$/.test(error)) {
    return `The password you are entering is too short(must be at least ${minPasswordLength} characters).`;
  }
  switch (error) {
    case 'NO SPECIAL':
      return 'The password you are entering does not contain any special characters(\'"!#%&()*+,-.:;<=>?[]^_`{|}~/$@).';
    case 'NO DIGIT':
      return 'The password you are entering does not contain any digits(0-9).';
    case 'NO LOWERCASE':
      return 'The password you are entering does not contain any lower case characters(a-z).';
    case 'NO UPPERCASE':
      return 'The password you are entering does not contain any upper case characters(A-Z).';
    case 'MISMATCH':
      return 'The password you are entering does not match the confirmed value.';
    default:
      return null;
  }
}

/**
 * Live-validation messages used inline beneath the password input. Mirrors
 * the message map from `customer/javascript/plugins/PasswordValidate/password-validate.js`.
 */
const LIVE_MESSAGES: Record<string, string> = {
  GENERIC: 'insecure password, please try again',
  UNCONFIRMED: 'passwords do not match',
  BLANK: 'please fill out this field',
  'TOO SHORT': 'must be at least 8 characters',
  'NO DIGIT': 'password does not contain any numbers',
  'NO LOWERCASE': 'password does not contain any lower-case characters',
  'NO UPPERCASE': 'password does not contain any upper-case characters',
  'NO SPECIAL': 'password does not contain any special characters',
  'INVALID SPECIAL': 'password contains an invalid character',
  GUESSABLE: 'password contains text that matches some of your personal data: ',
};

/**
 * Pick the first matching legacy message for an `errors` array from
 * `/verify_password.cgi`. Falls back to GENERIC. Special-cases "GUESSABLE:<text>".
 */
function getLiveMessage(errors: string[]): string {
  let key = 'GENERIC';
  let guessable = '';
  for (const code of errors) {
    if (code in LIVE_MESSAGES) {
      key = code;
      break;
    }
    if (code.indexOf('GUESSABLE') > -1) {
      key = 'GUESSABLE';
      guessable = code.split(':')[1] ?? '';
    }
  }
  return LIVE_MESSAGES[key] + guessable;
}

const VERIFY_URL = '/verify_password.cgi';
const DEBOUNCE_MS = 100;

export const ChangePasswordPage: React.FC<ChangePasswordPageProps> = ({
  state,
  token = '',
  error,
  minPasswordLength,
  expiredPassword = false,
  fetchFn,
}) => {
  if (state === 'expired-token') {
    return (
      <div className="hi-ui-change-password-page">
        <Text variant="heading-1">Expired Token</Text>
        <Text variant="body">
          Your token has expired. Please request a new one <Link href="/recover.cgi">here</Link>.
        </Text>
      </div>
    );
  }

  if (state === 'contact-support') {
    return (
      <div className="hi-ui-change-password-page">
        <Text variant="heading-1">Please Contact Support</Text>
        <Text variant="body">
          There was a problem with your request.&nbsp;&nbsp;Please contact support.
        </Text>
      </div>
    );
  }

  return (
    <ChangePasswordForm
      token={token}
      error={error}
      minPasswordLength={minPasswordLength}
      expiredPassword={expiredPassword}
      fetchFn={fetchFn}
    />
  );
};

interface ChangePasswordFormProps {
  token: string;
  error?: string;
  minPasswordLength: number;
  expiredPassword: boolean;
  fetchFn?: typeof fetch;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  token,
  error,
  minPasswordLength,
  expiredPassword,
  fetchFn,
}) => {
  const fetcher = fetchFn || fetch;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const serverError = error ? getServerErrorMessage(error, minPasswordLength) : null;

  // Live validation against /verify_password.cgi (mirrors legacy
  // password-validate.js — debounced 100ms, short-circuit if length < 4).
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (password.length < 4) {
      /* eslint-disable react-hooks/set-state-in-effect -- intentional: short-circuit live validation when input is empty or too short */
      setPasswordValid(false);
      setPasswordError(password.length === 0 ? null : LIVE_MESSAGES['TOO SHORT']);
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetcher(VERIFY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          customer_data: { password, confirm_password: confirmPassword, token },
        }),
      })
        .then((response) => response.json())
        .then((response: { valid?: number; errors?: string[] }) => {
          if (Number(response.valid) === 1) {
            setPasswordValid(true);
            setPasswordError(null);
          } else {
            setPasswordValid(false);
            setPasswordError(getLiveMessage(response.errors ?? []));
          }
        })
        .catch(() => {
          // Mirror legacy: "fake a success and let the password get
          // validated on form submit" if the AJAX call itself errors.
          setPasswordValid(true);
          setPasswordError(null);
        });
    }, DEBOUNCE_MS);

    // Cleanup only registered when password.length >= 4 (the early-return
    // path above doesn't return one), so debounceRef.current is always
    // truthy here — no need for a defensive null check.
    return () => clearTimeout(debounceRef.current!);
    // confirmPassword and token are intentionally not deps — they're sent
    // along but live-validation only fires on password change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  // Confirm-field equality check. Hide the error while the user is still
  // typing and the entered prefix matches (mirrors legacy `matchesSoFar`).
  const confirmMatches = password.length > 0 && password === confirmPassword;
  const confirmMatchesSoFar = password.startsWith(confirmPassword);
  const confirmError =
    confirmTouched && !confirmMatches && !confirmMatchesSoFar ? LIVE_MESSAGES.UNCONFIRMED : null;

  const handleSubmit = (_data: FormData, event: React.FormEvent<HTMLFormElement>) => {
    // hi-ui Form has already called event.preventDefault() before invoking
    // this handler. Gate the actual POST on validation; if it passes, fire
    // the native submit (which bypasses the synthetic onSubmit handler so
    // we don't recurse).
    if (!passwordValid || !confirmMatches) {
      if (!passwordValid) {
        setPasswordError(LIVE_MESSAGES.GENERIC);
      }
      if (!confirmMatches) {
        setConfirmTouched(true);
      }
      return;
    }
    event.currentTarget.submit();
  };

  return (
    <div className="hi-ui-change-password-page">
      {serverError && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            {serverError}
          </Text>
        </Notice>
      )}

      {expiredPassword && (
        <Text variant="body" align="center" bold>
          Your password has expired. Please change it now.
        </Text>
      )}

      <Text variant="heading-1">Change Password</Text>

      <Form action="/change-password.cgi" method="post" onSubmit={handleSubmit}>
        <Text variant="body">
          Please select a new secure password below to access your account and continue managing
          your services.
        </Text>

        <div className="hi-ui-change-password-page__fields">
          <FormField
            label="Password"
            error={passwordError ?? undefined}
            success={passwordValid && password.length > 0 ? '' : undefined}
          >
            <Input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              state={
                passwordError
                  ? 'error'
                  : passwordValid && password.length > 0
                    ? 'success'
                    : 'default'
              }
              maxLength={160}
            />
          </FormField>

          <FormField label="Confirm password" error={confirmError ?? undefined}>
            <Input
              type="password"
              name="confirm_password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (e.target.value.length > 0) setConfirmTouched(true);
              }}
              state={
                confirmError
                  ? 'error'
                  : confirmMatches && confirmPassword.length > 0
                    ? 'success'
                    : 'default'
              }
              maxLength={160}
            />
          </FormField>
        </div>

        <input type="hidden" name="token" value={token} />

        <div className="hi-ui-change-password-page__submit">
          <Button type="submit" variant="primary">
            CHANGE PASSWORD
          </Button>
        </div>
      </Form>
    </div>
  );
};
